import {
  faBox,
  faCheck,
  faFilter,
  faInfoCircle,
  faPen,
  faPlus,
  faSearch,
  faTimes,
  faTrash,
  faCog,
  faChevronLeft,
  faChevronRight,
  faPercent,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "bootstrap/dist/css/bootstrap.min.css";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAuthenticatedAPI } from "../hooks/useAuthenticatedAPI";
import ProcessLoading from "../components/processLoading";
import Select from "react-select";
import "../styles/gestionArticle.css";

const AdminArticlesPage = () => {
  const { client, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { apiGet, apiPost, apiPut, apiDelete, API_BASE_URL } =
    useAuthenticatedAPI();

  // États principaux
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [marques, setMarques] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);

  // États pour les filtres et recherche
  const [searchTerm, setSearchTerm] = useState("");
  const [marqueFilter, setMarqueFilter] = useState("Tous");
  const [categorieFilter, setCategorieFilter] = useState("Tous");
  const [isSearching, setIsSearching] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [filterResults, setFilterResults] = useState([]);

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  // États pour le modal
  const [showModal, setShowModal] = useState(false);
  const [currentArticle, setCurrentArticle] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewImages, setPreviewImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);

  // États de chargement et erreurs
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // NOUVEAU : Erreurs de spécifications
  const [specificationErrors, setSpecificationErrors] = useState([]);

  const [formatSpecificationError, setFormatSpecificationError] = useState("");

  // Vérification des autorisations au chargement
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/");
      return;
    }

    if (client?.role === "client") {
      setError(
        "Vous n'avez pas les autorisations nécessaires pour accéder à cette page"
      );
      setTimeout(() => navigate("/dashboard"), 2000);
      return;
    }

    loadInitialData();
  }, [client, isAuthenticated, navigate]);

  // Chargement initial des données
  const loadInitialData = async () => {
    setPageLoading(true);
    try {
      await Promise.all([fetchArticles(), fetchCategories(), fetchMarques()]);
    } catch (err) {
      setError("Erreur lors du chargement des données");
      console.error("Erreur:", err);
    } finally {
      setPageLoading(false);
    }
  };

  // Récupérer tous les articles
  const fetchArticles = async () => {
    try {
      const data = await apiGet("/article/get");
      const articlesArray = Array.isArray(data.articles) ? data.articles : [];
      setArticles(articlesArray);
      if (articlesArray.length > 0 && !selectedArticle) {
        setSelectedArticle(articlesArray[0]);
      }
    } catch (err) {
      console.error("Erreur fetch articles:", err);
      setError(err.message || "Erreur lors de la récupération des articles");
    }
  };

  // Récupérer toutes les catégories
  const fetchCategories = async () => {
    try {
      const data = await apiGet("/cat/get");
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erreur fetch categories:", err);
    }
  };

  // Récupérer toutes les marques
  const fetchMarques = async () => {
    try {
      const data = await apiGet("/marque/get");
      setMarques(Array.isArray(data.marques) ? data.marques : []);
    } catch (err) {
      console.error("Erreur fetch marques:", err);
    }
  };

  // Options pour les sélecteurs de filtres - FORMAT REACT-SELECT
  const marqueFilterOptions = [
    { value: "Tous", label: "Toutes les marques" },
    ...marques.map((marque) => ({ value: marque.nom, label: marque.nom })),
  ];

  const categorieFilterOptions = [
    { value: "Tous", label: "Toutes les catégories" },
    ...categories.map((cat) => ({ value: cat.nom, label: cat.nom })),
  ];

  // Options pour le formulaire modal
  const categorieOptions = categories.map((cat) => ({
    value: cat.id_cat,
    label: cat.nom,
  }));

  const marqueOptions = marques.map((marque) => ({
    value: marque.id_marq,
    label: marque.nom,
  }));

  // Recherche frontend d'articles par nom
  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setIsSearching(false);
      setIsFiltering(false);
      setFilterResults([]);
      return;
    }

    const searchResults = articles.filter(
      (article) =>
        article.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getMarqueName(article.id_marq)
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        getCategoryName(article.id_cat)
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
    );

    setFilterResults(searchResults);
    setIsSearching(true);
    setIsFiltering(false);
    setCurrentPage(1);
  };

  // Filtrage par catégorie
  const handleCategoryFilter = async (selected) => {
    const categoryName = selected?.value || "Tous";
    setCategorieFilter(categoryName);

    if (categoryName === "Tous") {
      setIsFiltering(false);
      setFilterResults([]);
      setIsSearching(false);
      return;
    }

    const category = categories.find((cat) => cat.nom === categoryName);
    if (!category) return;

    setLoading(true);
    try {
      const data = await apiGet(`/article/category/${category.id_cat}`);
      setFilterResults(Array.isArray(data.articles) ? data.articles : []);
      setIsFiltering(true);
      setIsSearching(false);
      setCurrentPage(1);
    } catch (err) {
      console.error("Erreur filtre catégorie:", err);
      setError("Erreur lors du filtrage par catégorie");
    } finally {
      setLoading(false);
    }
  };

  // Filtrage par marque
  const handleMarqueFilter = async (selected) => {
    const marqueName = selected?.value || "Tous";
    setMarqueFilter(marqueName);

    if (marqueName === "Tous") {
      setIsFiltering(false);
      setFilterResults([]);
      setIsSearching(false);
      return;
    }

    const marque = marques.find((m) => m.nom === marqueName);
    if (!marque) return;

    setLoading(true);
    try {
      const data = await apiGet(`/article/brand/${marque.id_marq}`);
      setFilterResults(Array.isArray(data.articles) ? data.articles : []);
      setIsFiltering(true);
      setIsSearching(false);
      setCurrentPage(1);
    } catch (err) {
      console.error("Erreur filtre marque:", err);
      setError("Erreur lors du filtrage par marque");
    } finally {
      setLoading(false);
    }
  };

  // Réinitialiser la recherche et les filtres
  const clearSearch = () => {
    setSearchTerm("");
    setIsSearching(false);
    setIsFiltering(false);
    setFilterResults([]);
    setMarqueFilter("Tous");
    setCategorieFilter("Tous");
    setCurrentPage(1);
  };

  // Parser la description pour séparer description et spécifications
  const parseDescription = (description) => {
    if (!description) return { description: "", specifications: [] };
    const parts = description.split("##SPEC##");
    return {
      description: parts[0] || "",
      specifications: parts.slice(1).filter((spec) => spec.trim()),
    };
  };

  // Combiner description et spécifications
  const combineDescription = (desc, specs) => {
    const cleanSpecs = specs.filter((spec) => spec.trim());
    if (cleanSpecs.length === 0) return desc;
    return `${desc}##SPEC##${cleanSpecs.join("##SPEC##")}`;
  };

  // Obtenir les articles à afficher
  const getDisplayedArticles = () => {
    if (isSearching || isFiltering) return filterResults;
    return articles;
  };

  // Pagination
  const displayedArticles = getDisplayedArticles();
  const totalPages = Math.ceil(displayedArticles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedArticles = displayedArticles.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Fonctions du modal
  const openCreateModal = () => {
    setIsEditing(false);
    setCurrentArticle({
      nom: "",
      description: "",
      specifications: [""],
      prix: "",
      id_cat: "",
      id_marq: "",
      quantite: 100,
      reduction: 0,
      note: 0,
      images: [],
    });
    setPreviewImage(null);
    setPreviewImages([]);
    setImagesToDelete([]);
    setShowModal(true);
  };

  const openEditModal = (article) => {
    setIsEditing(true);
    const parsed = parseDescription(article.description);
    const imageArray = article.image
      ? article.image
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

    setCurrentArticle({
      ...article,
      description: parsed.description,
      specifications:
        parsed.specifications.length > 0 ? parsed.specifications : [""],
      images: imageArray,
    });

    const previews = imageArray.map(
      (p) => `https://boutiqu.transfoafricainc.com/${p}`
    );
    setPreviewImages(previews);
    setPreviewImage(previews[0] || null);
    setImagesToDelete([]);
    setShowModal(true);
  };

  // Validation spécification
  const validateSpecification = (spec) => {
    const t = spec.trim();
    if (!t) return true;
    return /^[^:]+:\s*.+$/.test(t);
  };

  // Gestion des spécifications
  const addSpecification = () => {
    const hasInvalid = currentArticle.specifications.some(
      (s, i) => s.trim() && !validateSpecification(s)
    );
    if (hasInvalid) {
      setFormatSpecificationError(
        "Corrigez les spécifications invalides avant d'en ajouter."
      );
      return;
    }
    setCurrentArticle((prev) => ({
      ...prev,
      specifications: [...prev.specifications, ""],
    }));
    setSpecificationErrors((prev) => [...prev, false]);
  };

  const removeSpecification = (index) => {
    const newSpecs = currentArticle.specifications.filter(
      (_, i) => i !== index
    );
    setCurrentArticle({
      ...currentArticle,
      specifications: newSpecs.length > 0 ? newSpecs : [""],
    });
  };

  const updateSpecification = (index, value) => {
    const newSpecs = [...currentArticle.specifications];
    newSpecs[index] = value;
    setCurrentArticle({
      ...currentArticle,
      specifications: newSpecs,
    });

    // Validation en temps réel
    const newErrors = [...specificationErrors];
    if (value.trim()) {
      newErrors[index] = !validateSpecification(value);
    } else {
      newErrors[index] = false;
    }
    setSpecificationErrors(newErrors);

    // Masquer l'erreur générale si tout est bon
    const hasInvalid = newErrors.some((err) => err);
    if (!hasInvalid) {
      setFormatSpecificationError("");
    }
  };

  // Gestion du formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentArticle((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setCurrentArticle((prev) => {
      const existing = prev?.images || [];
      return {
        ...prev,
        images: [...existing, ...files],
      };
    });

    const newPreviews = files.map((f) => URL.createObjectURL(f));
    setPreviewImages((prev) => [...prev, ...newPreviews]);
  };

  const handleSelectChange = (selected, actionMeta) => {
    const { name } = actionMeta;
    setCurrentArticle((prev) => ({
      ...prev,
      [name]: selected?.value || "",
    }));
  };

  // Supprimer une image depuis le modal
  const removeImageFromCurrent = (index) => {
    setCurrentArticle((prev) => {
      if (!prev) return prev;
      const images = [...(prev.images || [])];
      const removed = images.splice(index, 1)[0];

      if (typeof removed === "string") {
        setImagesToDelete((prevDel) => [...prevDel, removed]);
      }

      return {
        ...prev,
        images,
      };
    });

    setPreviewImages((prev) => {
      const p = [...prev];
      const removedPreview = p.splice(index, 1);
      if (removedPreview[0] && removedPreview[0].startsWith("blob:")) {
        URL.revokeObjectURL(removedPreview[0]);
      }
      return p;
    });
  };

  // Sauvegarde
  const handleSave = async () => {
    setLoading(true);

    const invalid = currentArticle.specifications
      .map((s, i) => ({ s: s.trim(), i }))
      .filter(({ s }) => s && !validateSpecification(s));

    if (invalid.length > 0) {
      setError(
        "Certaines spécifications ne respectent pas le format `Nom: Valeur`"
      );
      setSpecificationErrors((prev) =>
        prev.map((_, i) => invalid.some((v) => v.i === i))
      );
      setLoading(false);
      return;
    }

    if (
      !currentArticle.nom ||
      !currentArticle.id_cat ||
      !currentArticle.id_marq ||
      !currentArticle.prix
    ) {
      setError("Champs obligatoires manquants");
      setLoading(false);
      return;
    }

    const formData = new FormData();
    const cleanSpecs = currentArticle.specifications.filter((s) => s.trim());
    formData.append("nom", currentArticle.nom.trim());
    formData.append(
      "description",
      combineDescription(currentArticle.description || "", cleanSpecs)
    );
    formData.append("prix", parseFloat(currentArticle.prix));
    formData.append("id_cat", parseInt(currentArticle.id_cat));
    formData.append("id_marq", parseInt(currentArticle.id_marq));
    formData.append("quantite", parseInt(currentArticle.quantite) || 0);
    formData.append("reduction", parseFloat(currentArticle.reduction) || 0);
    formData.append("note", parseInt(currentArticle.note) || 0);

    // Gestion de plusieurs images
    if (Array.isArray(currentArticle.images)) {
      currentArticle.images.forEach((img) => {
        if (img instanceof File) {
          formData.append("images", img);
        }
      });
    }

    if (isEditing) {
      if (imagesToDelete.length > 0) {
        formData.append("imagesToDelete", JSON.stringify(imagesToDelete));
      } else {
        formData.append("imagesToDelete", JSON.stringify([]));
      }
    }

    try {
      let response;
      if (isEditing) {
        const articleId = currentArticle.id_art || currentArticle.id;
        if (!articleId) {
          throw new Error("ID de l'article non trouvé pour la modification");
        }
        response = await apiPut(`/article/put/${articleId}`, formData);
      } else {
        response = await apiPost("/article/create", formData);
      }

      setSuccess(
        isEditing ? "Article modifié avec succès" : "Article créé avec succès"
      );
      setShowModal(false);

      previewImages.forEach((p) => {
        if (p && p.startsWith("blob:")) URL.revokeObjectURL(p);
      });
      setPreviewImages([]);
      setImagesToDelete([]);
      await fetchArticles();

      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Erreur lors de la sauvegarde:", err);
      setError(err.message || "Erreur lors de la sauvegarde");
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Suppression
  const handleDelete = async (id_art) => {
    if (!window.confirm("Voulez-vous vraiment supprimer cet article ?")) return;

    setLoading(true);
    try {
      await apiDelete(`/article/del/${id_art}`);
      setSuccess("Article supprimé avec succès");

      if (selectedArticle?.id_art === id_art) {
        setSelectedArticle(
          articles.length > 1 ? articles.find((a) => a.id_art !== id_art) : null
        );
      }

      await fetchArticles();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message || "Erreur lors de la suppression");
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Navigation vers gestion des catégories et marques
  const goToCategoriesManagement = () => {
    navigate("/gestion-categories-marques");
  };

  // Obtenir le nom de la catégorie
  const getCategoryName = (id_cat) => {
    const category = categories.find((cat) => cat.id_cat === id_cat);
    return category ? category.nom : "Non définie";
  };

  // Obtenir le nom de la marque
  const getMarqueName = (id_marq) => {
    const marque = marques.find((marq) => marq.id_marq === id_marq);
    return marque ? marque.nom : "Non définie";
  };

  if (pageLoading) {
    return <ProcessLoading message="Chargement des articles..." />;
  }

  return (
    <div className="container-fluid p-4 bg-light">
      {/* Messages d'erreur et succès */}
      {error && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError("")}
          ></button>
        </div>
      )}

      {success && (
        <div
          className="alert alert-success alert-dismissible fade show"
          role="alert"
        >
          {success}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccess("")}
          ></button>
        </div>
      )}

      {/* En-tête */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-0">
            <FontAwesomeIcon icon={faBox} className="me-2 text-primary" />
            Gestion des Articles (Administration)
          </h4>
          <small className="text-muted">
            {isSearching
              ? `${displayedArticles.length} résultat(s) de recherche`
              : isFiltering
              ? `${displayedArticles.length} article(s) filtré(s)`
              : `${displayedArticles.length} article(s)`}{" "}
            • Page {currentPage}/{totalPages}
          </small>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-secondary"
            onClick={goToCategoriesManagement}
          >
            <FontAwesomeIcon icon={faCog} className="me-2" />
            Gérer Catégories/Marques
          </button>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Nouvel Article
          </button>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h5 className="card-title mb-3">
            <FontAwesomeIcon icon={faSearch} className="me-2" />
            Rechercher & Filtrer
          </h5>
          <div className="row g-3">
            <div className="col-md-4">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Rechercher un article par nom..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleSearch}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faSearch} />
                </button>
                {(isSearching || isFiltering) && (
                  <button className="btn btn-secondary" onClick={clearSearch}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
              </div>
            </div>
            <div className="col-md-4">
              <Select
                options={marqueFilterOptions}
                value={
                  marqueFilterOptions.find(
                    (option) => option.value === marqueFilter
                  ) || marqueFilterOptions[0]
                }
                onChange={handleMarqueFilter}
                placeholder="-- Sélectionner une marque --"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
                isSearchable={false}
              />
            </div>
            <div className="col-md-4">
              <Select
                options={categorieFilterOptions}
                value={
                  categorieFilterOptions.find(
                    (option) => option.value === categorieFilter
                  ) || categorieFilterOptions[0]
                }
                onChange={handleCategoryFilter}
                placeholder="-- Sélectionner une catégorie --"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
                isSearchable={false}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Message si aucun résultat */}
      {(isSearching || isFiltering) && displayedArticles.length === 0 && (
        <div className="alert alert-info text-center">
          <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
          {isSearching
            ? `Aucun article trouvé pour "${searchTerm}"`
            : `Aucun article trouvé pour le filtre sélectionné`}
        </div>
      )}

      {/* Contenu principal */}
      <div className="row">
        {/* Liste des articles */}
        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>ID</th>
                      <th>Image</th>
                      <th>Produit</th>
                      <th>Marque</th>
                      <th>Catégorie</th>
                      <th>Stock</th>
                      <th>Prix</th>
                      <th>Réduction</th>
                      <th>Note</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedArticles.map((article) => (
                      <tr
                        key={article.id_art}
                        onClick={() => setSelectedArticle(article)}
                        className={
                          selectedArticle?.id_art === article.id_art
                            ? "table-primary"
                            : ""
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <td className="fw-bold">{article.id_art}</td>
                        <td>
                          {article.image ? (
                            (() => {
                              const first = article.image
                                .split(",")
                                .map((s) => s.trim())
                                .filter(Boolean)[0];
                              const src = first
                                ? `https://boutiqu.transfoafricainc.com/${first}`
                                : null;
                              return src ? (
                                <img
                                  src={src}
                                  alt={article.nom}
                                  style={{
                                    width: "50px",
                                    height: "50px",
                                    objectFit: "cover",
                                    objectPosition: "center",
                                  }}
                                  className="rounded border"
                                />
                              ) : (
                                <div
                                  className="bg-light rounded border d-flex align-items-center justify-content-center"
                                  style={{ width: "50px", height: "50px" }}
                                >
                                  <FontAwesomeIcon
                                    icon={faBox}
                                    className="text-muted"
                                  />
                                </div>
                              );
                            })()
                          ) : (
                            <div
                              className="bg-light rounded border d-flex align-items-center justify-content-center"
                              style={{ width: "50px", height: "50px" }}
                            >
                              <FontAwesomeIcon
                                icon={faBox}
                                className="text-muted"
                              />
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="fw-bold">{article.nom}</div>
                          <small className="text-muted">
                            {article.description?.substring(0, 30)}...
                          </small>
                        </td>
                        <td>{getMarqueName(article.id_marq)}</td>
                        <td>{getCategoryName(article.id_cat)}</td>
                        <td>
                          <span
                            className={`badge ${
                              article.quantite > 0 ? "bg-success" : "bg-danger"
                            }`}
                          >
                            {article.quantite}
                          </span>
                        </td>
                        <td className="fw-bold">{article.prix} FCFA</td>
                        <td>
                          {article.reduction > 0 ? (
                            <span className="badge bg-warning text-dark">
                              <FontAwesomeIcon
                                icon={faPercent}
                                className="me-1"
                              />
                              {article.reduction}%
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              article.note >= 4
                                ? "bg-success"
                                : article.note >= 3
                                ? "bg-warning text-dark"
                                : article.note >= 2
                                ? "bg-info"
                                : article.note >= 1
                                ? "bg-secondary"
                                : "bg-danger"
                            }`}
                          >
                            {article.note || 0}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-danger me-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(article.id_art);
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditModal(article);
                            }}
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="card-footer">
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    Affichage de {startIndex + 1} à{" "}
                    {Math.min(
                      startIndex + itemsPerPage,
                      displayedArticles.length
                    )}
                    sur {displayedArticles.length} articles
                  </small>
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li
                        className={`page-item ${
                          currentPage === 1 ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                      </li>

                      {[...Array(totalPages)].map((_, i) => {
                        const page = i + 1;
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 2 && page <= currentPage + 2)
                        ) {
                          return (
                            <li
                              key={page}
                              className={`page-item ${
                                currentPage === page ? "active" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => setCurrentPage(page)}
                              >
                                {page}
                              </button>
                            </li>
                          );
                        } else if (
                          page === currentPage - 3 ||
                          page === currentPage + 3
                        ) {
                          return (
                            <li key={page} className="page-item disabled">
                              <span className="page-link">...</span>
                            </li>
                          );
                        }
                        return null;
                      })}

                      <li
                        className={`page-item ${
                          currentPage === totalPages ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Détails de l'article */}
        <div className="col-lg-4">
          {selectedArticle ? (
            <div className="card shadow-sm h-100">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">Détails de l'article</h5>
              </div>
              <div className="card-body">
                <div className="text-center mb-3">
                  {selectedArticle.image ? (
                    (() => {
                      const imgs = selectedArticle.image
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean);
                      const first = imgs[0];
                      const src = first
                        ? `https://boutiqu.transfoafricainc.com/${first}`
                        : null;
                      return src ? (
                        <img
                          src={src}
                          alt={selectedArticle.nom}
                          className="img-fluid rounded border"
                          style={{
                            maxHeight: "200px",
                            width: "200px",
                            objectFit: "cover",
                            objectPosition: "center",
                          }}
                        />
                      ) : (
                        <div
                          className="bg-light rounded border d-inline-flex align-items-center justify-content-center"
                          style={{ width: "200px", height: "200px" }}
                        >
                          <FontAwesomeIcon
                            icon={faBox}
                            size="3x"
                            className="text-secondary"
                          />
                        </div>
                      );
                    })()
                  ) : (
                    <div
                      className="bg-light rounded border d-inline-flex align-items-center justify-content-center"
                      style={{ width: "200px", height: "200px" }}
                    >
                      <FontAwesomeIcon
                        icon={faBox}
                        size="3x"
                        className="text-secondary"
                      />
                    </div>
                  )}
                </div>

                <h5 className="text-primary">{selectedArticle.nom}</h5>
                <p className="text-muted mb-3">
                  {getMarqueName(selectedArticle.id_marq)}
                </p>

                {/* Gallery: show all images as thumbnails */}
                {selectedArticle.image &&
                  (() => {
                    const imgs = selectedArticle.image
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean);
                    if (imgs.length <= 1) return null;
                    return (
                      <div className="mb-3">
                        <h6 className="text-muted">Galerie</h6>
                        <div className="d-flex gap-2 flex-wrap">
                          {imgs.map((p, i) => (
                            <img
                              key={i}
                              src={`https://boutiqu.transfoafricainc.com/${p}`}
                              alt={`${selectedArticle.nom}-${i}`}
                              style={{
                                width: 60,
                                height: 60,
                                objectFit: "cover",
                                objectPosition: "center",
                              }}
                              className="rounded border"
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                <div className="mb-3">
                  <h6 className="text-muted">INFORMATIONS</h6>
                  <hr className="mt-1" />
                  <div className="row">
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">ID</small>
                      <span>{selectedArticle.id_art}</span>
                    </div>
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">Catégorie</small>
                      <span>{getCategoryName(selectedArticle.id_cat)}</span>
                    </div>
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">Prix</small>
                      <span className="fw-bold">
                        {selectedArticle.prix} FCFA
                      </span>
                    </div>
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">Stock</small>
                      <span
                        className={`badge ${
                          selectedArticle.quantite > 0
                            ? "bg-success"
                            : "bg-danger"
                        }`}
                      >
                        {selectedArticle.quantite} unités
                      </span>
                    </div>
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">Réduction</small>
                      <span>{selectedArticle.reduction || 0}%</span>
                    </div>
                    <div className="col-6 mb-2">
                      <small className="text-muted d-block">Note</small>
                      <span
                        className={`badge ${
                          selectedArticle.note >= 4
                            ? "bg-success"
                            : selectedArticle.note >= 3
                            ? "bg-warning text-dark"
                            : selectedArticle.note >= 2
                            ? "bg-info"
                            : selectedArticle.note >= 1
                            ? "bg-secondary"
                            : "bg-danger"
                        }`}
                      >
                        {selectedArticle.note || 0}
                      </span>
                    </div>
                  </div>
                </div>

                {(() => {
                  const parsed = parseDescription(selectedArticle.description);
                  return (
                    <>
                      {parsed.description && (
                        <div className="mb-3">
                          <h6 className="text-muted">DESCRIPTION</h6>
                          <hr className="mt-1" />
                          <p>
                            <FontAwesomeIcon
                              icon={faInfoCircle}
                              className="me-2 text-muted"
                            />
                            {parsed.description}
                          </p>
                        </div>
                      )}

                      {parsed.specifications.length > 0 && (
                        <div className="mb-3">
                          <h6 className="text-muted">SPÉCIFICATIONS</h6>
                          <hr className="mt-1" />
                          <ul className="list-unstyled">
                            {parsed.specifications.map((spec, i) => (
                              <li key={i} className="mb-1">
                                <FontAwesomeIcon
                                  icon={faCheck}
                                  className="text-success me-2"
                                />
                                {spec}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  );
                })()}

                <div className="d-grid gap-2 mt-4">
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => openEditModal(selectedArticle)}
                  >
                    <FontAwesomeIcon icon={faPen} className="me-2" />
                    Modifier l'article
                  </button>
                  <button
                    className="btn btn-outline-danger"
                    onClick={() => handleDelete(selectedArticle.id_art)}
                  >
                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                    Supprimer l'article
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="card shadow-sm h-100">
              <div className="card-body d-flex align-items-center justify-content-center">
                <p className="text-muted mb-0">
                  Sélectionnez un article pour afficher les détails
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal pour ajouter/modifier */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: "900px" }}>
            {formatSpecificationError && (
              <div
                className="alert alert-danger alert-dismissible fade show"
                role="alert"
              >
                {formatSpecificationError}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError("")}
                ></button>
              </div>
            )}
            <div className="modal-header">
              <h5>{isEditing ? "Modifier" : "Ajouter"} un article</h5>
              <button className="btn-close" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div
              className="modal-body"
              style={{ maxHeight: "70vh", overflowY: "auto" }}
            >
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Nom *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="nom"
                      value={currentArticle.nom}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Catégorie *</label>
                      <Select
                        options={categorieOptions}
                        value={categorieOptions.find(
                          (o) => o.value === parseInt(currentArticle.id_cat)
                        )}
                        onChange={(s) =>
                          handleSelectChange(s, { name: "id_cat" })
                        }
                        placeholder="--"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                        isSearchable={false}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Marque *</label>
                      <Select
                        options={marqueOptions}
                        value={marqueOptions.find(
                          (o) => o.value === parseInt(currentArticle.id_marq)
                        )}
                        onChange={(s) =>
                          handleSelectChange(s, { name: "id_marq" })
                        }
                        placeholder="--"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                        isSearchable={false}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Prix (FCFA) *</label>
                      <input
                        type="number"
                        className="form-control"
                        name="prix"
                        value={currentArticle.prix}
                        onChange={handleInputChange}
                        min="0"
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Quantité *</label>
                      <input
                        type="number"
                        className="form-control"
                        name="quantite"
                        value={currentArticle.quantite}
                        onChange={handleInputChange}
                        min="0"
                        required
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Réduction (%)</label>
                      <input
                        type="number"
                        className="form-control"
                        name="reduction"
                        value={currentArticle.reduction}
                        onChange={handleInputChange}
                        min="0"
                        max="100"
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Images du produit</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleImageChange}
                      multiple
                    />
                    <small className="form-text text-muted">
                      Vous pouvez ajouter plusieurs images (max 5 côté serveur)
                    </small>
                  </div>
                </div>

                <div className="col-md-6">
                  {previewImages.length > 0 && (
                    <div className="mb-3 text-center">
                      <div className="d-flex gap-2 flex-wrap justify-content-center">
                        {previewImages.map((p, i) => (
                          <div key={i} className="position-relative">
                            <img
                              src={p}
                              alt={`Aperçu-${i}`}
                              className="img-fluid rounded border"
                              style={{
                                maxHeight: "120px",
                                width: "120px",
                                objectFit: "cover",
                                objectPosition: "center",
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-danger position-absolute"
                              style={{ top: 4, right: 4 }}
                              onClick={() => removeImageFromCurrent(i)}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      rows="4"
                      value={currentArticle.description}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* SPÉCIFICATIONS STYLÉES */}
                  <div className="mb-4">
                    <label className="form-label d-block fw-bold">
                      Spécifications
                    </label>
                    <div className="alert alert-warning border border-warning rounded p-2 mb-3 d-flex align-items-start shadow-sm">
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="text-warning me-2 mt-1 flex-shrink-0"
                      />
                      <div className="small">
                        <strong>Format requis :</strong>{" "}
                        <code className="bg-light px-1 rounded">
                          Nom: Valeur
                        </code>
                        <br />
                        <em>Exemples :</em>{" "}
                        <code className="bg-light px-1 rounded">
                          Résolution: 1200x1200 dpi
                        </code>
                        ,{" "}
                        <code className="bg-light px-1 rounded">
                          Poids: 1.5 kg
                        </code>
                      </div>
                    </div>

                    {currentArticle.specifications.map((spec, i) => (
                      <div key={i} className="input-group mb-2">
                        <input
                          type="text"
                          className={`form-control ${
                            specificationErrors[i] ? "is-invalid" : ""
                          }`}
                          value={spec}
                          onChange={(e) =>
                            updateSpecification(i, e.target.value)
                          }
                          placeholder={`Ex ${i + 1}: Résolution: 1200x1200 dpi`}
                          style={{
                            borderRadius: "0.375rem 0 0 0.375rem",
                            ...(specificationErrors[i] && {
                              borderColor: "#dc3545",
                              boxShadow: "0 0 0 0.2rem rgba(220,53,69,0.25)",
                            }),
                          }}
                        />
                        {specificationErrors[i] && (
                          <div
                            className="invalid-feedback d-block position-absolute"
                            style={{
                              top: "100%",
                              left: 0,
                              fontSize: "0.875em",
                            }}
                          >
                            Format invalide. Utilisez :{" "}
                            <strong>Nom: Valeur</strong>
                          </div>
                        )}
                        <button
                          className="btn btn-outline-danger"
                          type="button"
                          onClick={() => removeSpecification(i)}
                          disabled={currentArticle.specifications.length === 1}
                          style={{ borderRadius: "0 0.375rem 0.375rem 0" }}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    ))}

                    <button
                      className="btn btn-outline-primary btn-sm mt-4 d-flex align-items-center"
                      type="button"
                      onClick={addSpecification}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-1" /> Ajouter
                      une spécification
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
                disabled={loading}
              >
                Annuler
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={
                  loading ||
                  !currentArticle.nom ||
                  !currentArticle.id_cat ||
                  !currentArticle.id_marq ||
                  !currentArticle.prix
                }
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    {isEditing ? "Modification..." : "Création..."}
                  </>
                ) : (
                  <>{isEditing ? "Enregistrer" : "Ajouter"}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {loading && <ProcessLoading message="Traitement en cours..." />}
    </div>
  );
};

export default AdminArticlesPage;
